import { Alert, Button, Card, Col, Form, Row, Typography } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PermissionGate } from "@/shared/components/PermissionGate";
import { cronShapeOk, SettingFieldRenderer } from "../components/SettingFieldRenderer";
import {
	SETTING_GROUP_LABEL_KEYS,
	SETTING_GROUP_ORDER,
	SETTINGS_META,
	type SettingGroup,
	type SettingMeta,
} from "../config/settingsMeta";
import { useSaveSettingsGroup, useSettingsList } from "../hooks/useSettings";
import type { Setting, SettingValue } from "../types";

const { Title } = Typography;

/**
 * ⚠️ **DB sozlamasi EMAS** — frontend konstantasi (spec §6). Buyurtmachi
 * talabi aynan shu: bank tomonidan tahrirlanmaydi, o'zgartirish faqat kod
 * orqali (yangi deploy). Yangi `settings` yozuvi qo'shish bu talabni
 * buzardi — kelajakda admin uni panel orqali o'zgartira olardi.
 */
const SUPPORT_PHONE = "+998622277676";

type MetaEntry = [string, SettingMeta];

/**
 * `SETTINGS_META` bo'yicha GURUHLANGAN, spec §3 jadvalidagi TARTIBDA
 * (`Object.entries` string kalitlar uchun insertion order'ni saqlaydi).
 * Modul darajasida BIR MARTA hisoblanadi — `SETTINGS_META` statik konstanta,
 * har render'da qayta guruhlash keraksiz.
 */
const GROUPED_META: Map<SettingGroup, MetaEntry[]> = (() => {
	const map = new Map<SettingGroup, MetaEntry[]>();
	for (const group of SETTING_GROUP_ORDER) {
		map.set(group, []);
	}
	for (const entry of Object.entries(SETTINGS_META)) {
		const [, meta] = entry;
		map.get(meta.group)?.push(entry);
	}
	return map;
})();

/**
 * `GET /settings` ning `value`si `unknown` (`jsonb`). `SETTINGS_META`da
 * ro'ykhatdagi HAR kalit backend'da `string | number | boolean` sxemasiga
 * ega (`notification_channels`dan tashqari — u bu sahifada UMUMAN yo'q),
 * ya'ni amalda bu funksiya doim birinchi shoxobchaga tushadi. Qolgan
 * shoxobchalar faqat qator hali kelmagan holat uchun (test fixture,
 * birinchi render) — ekranni buzmaydigan oqilona standart beradi.
 */
function asSettingValue(raw: unknown, meta: SettingMeta): SettingValue {
	if (typeof raw === "string" || typeof raw === "number" || typeof raw === "boolean") {
		return raw;
	}

	if (meta.type === "number") {
		return meta.min;
	}

	if (meta.type === "boolean") {
		return false;
	}

	return "";
}

export function SettingsPage() {
	const { t } = useTranslation("settings");
	const { data: settings = [], isLoading, isError } = useSettingsList();
	const saveGroup = useSaveSettingsGroup();

	/**
	 * Qoralama holat — FAQAT tegilgan kalitlarni saqlaydi, serverdagi
	 * qiymatlarning nusxasini EMAS. Eski `SettingsPage` dagi ikkita
	 * o'lchangan regressiya shu naqsh bilan yopilgan (pastda takrorlanadi,
	 * chunki naqshning O'ZI bu faylda ham amal qiladi):
	 *
	 *   1. Nusxa `useEffect([settings])` bilan to'ldirilsa, `data` `undefined`
	 *      bo'lganda `useSettingsList()` har renderda YANGI `[]` qaytaradi —
	 *      dep har renderda o'zgaradi → `setDraft` → yana render → cheksiz sikl.
	 *   2. Nusxa saqlashdan keyingi `invalidateQueries` paytida BOSHQA
	 *      kartalarning kiritilgan, lekin hali saqlanmagan qiymatlarini
	 *      jimgina bekor qilardi.
	 *
	 * Ko'rsatiladigan qiymat — `draft[key] ?? asSettingValue(...)` (`??`,
	 * `||` emas: `0`, `false` va bo'sh satr ham haqiqiy qoralama).
	 */
	const [draft, setDraft] = useState<Partial<Record<string, SettingValue>>>({});

	const byKey = new Map<string, unknown>(
		settings.map((setting: Setting) => [setting.key, setting.value])
	);

	function currentValue(key: string, meta: SettingMeta): SettingValue {
		const touched = draft[key];
		return touched ?? asSettingValue(byKey.get(key), meta);
	}

	function handleFieldChange(key: string, value: SettingValue) {
		setDraft((prev) => ({ ...prev, [key]: value }));
	}

	function handleSaveGroup(group: SettingGroup) {
		const entries = (GROUPED_META.get(group) ?? [])
			.filter(([key]) => key in draft)
			.map(([key, meta]) => ({ key, value: draft[key] as SettingValue, meta }));

		if (entries.length === 0) {
			return;
		}

		// Cron shakli — aniq xato holatlarini oldindan tutish (spec §4);
		// yakuniy tekshiruv baribir backendda (`isValidCron`).
		const buzuqCron = entries.find(
			({ meta, value }) => meta.type === "cron" && !cronShapeOk(String(value))
		);
		if (buzuqCron) {
			return;
		}

		const savedKeys = new Set(entries.map((entry) => entry.key));

		saveGroup.mutate(
			{ group, entries: entries.map(({ key, value }) => ({ key, value })) },
			{
				// Saqlangan kalitlarni qoralamadan olib tashlaymiz — endi
				// serverdagi qiymat ko'rsatiladi. Qorovul kerak: so'rov
				// uchayotganda foydalanuvchi maydonni yana tahrirlagan bo'lsa,
				// uning yangi kiritmasini o'chirmaymiz (mavjud naqsh,
				// `useSettings.ts`ning eski `onSuccess`i bilan bir xil).
				onSuccess: () =>
					setDraft((prev) => {
						const next = { ...prev };
						for (const key of savedKeys) {
							if (next[key] === entries.find((entry) => entry.key === key)?.value) {
								delete next[key];
							}
						}
						return next;
					}),
			}
		);
	}

	return (
		<div>
			<Title level={3} className="mb-6">
				{t("page.title")}
			</Title>

			<Alert
				className="mb-4"
				type="info"
				showIcon
				title={t("page.bannerTitle")}
				description={t("page.bannerDescription", { phone: SUPPORT_PHONE })}
			/>

			{isError && (
				<Alert
					className="mb-4"
					type="error"
					showIcon
					title={t("page.loadErrorTitle")}
					description={t("page.loadErrorDescription")}
				/>
			)}

			<div className="flex flex-col gap-4">
				{SETTING_GROUP_ORDER.map((group) => {
					const entries = GROUPED_META.get(group) ?? [];
					const isSavingThisGroup = saveGroup.isPending && saveGroup.variables?.group === group;

					return (
						<Card key={group} title={t(SETTING_GROUP_LABEL_KEYS[group])} loading={isLoading}>
							<Form layout="vertical">
								<Row gutter={16}>
									{entries.map(([key, meta]) => (
										<Col key={key} xs={24} sm={12} md={8}>
											<Form.Item label={t(meta.labelKey)} htmlFor={key}>
												<SettingFieldRenderer
													settingKey={key}
													meta={meta}
													value={currentValue(key, meta)}
													onChange={(value) => handleFieldChange(key, value)}
												/>
											</Form.Item>
										</Col>
									))}
								</Row>
							</Form>

							<PermissionGate resource="settings" action="update">
								<Button
									type="primary"
									loading={isSavingThisGroup}
									onClick={() => handleSaveGroup(group)}
								>
									{t("page.saveButton")}
								</Button>
							</PermissionGate>
						</Card>
					);
				})}
			</div>
		</div>
	);
}
